/**
 * The small set of parts every screen is built from, so the app reads as one thing:
 * section headings, segmented switches, chips, callouts, stat tiles, skeletons and the
 * verdict colours.
 */

import type { ReactNode } from "react";
import { AlertTriangle, Info, OctagonAlert } from "lucide-react";
import type { Verdict } from "./api";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

export function SectionHeader({ title, action, hint }: { title: string; action?: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-3 flex items-end justify-between gap-3">
      <div>
        <div className="label">{title}</div>
        {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
      </div>
      {action}
    </div>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  size = "md",
  full = false,
}: {
  value: T;
  options: ReadonlyArray<{ value: T; label: ReactNode }>;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  full?: boolean;
}) {
  return (
    <div className={cx("inline-flex rounded-xl bg-slate-100 p-1", full && "flex w-full")} role="tablist">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cx(
              "whitespace-nowrap rounded-lg font-medium transition",
              size === "sm" ? "px-2.5 py-1 text-[13px]" : "px-3.5 py-1.5 text-sm",
              full && "flex-1",
              active ? "bg-white text-navy-900 shadow-sm" : "text-slate-500 hover:text-slate-800",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const CHIP_TONE = {
  slate: "bg-slate-100 text-slate-700",
  navy: "bg-navy-50 text-navy-800",
  gold: "bg-gold-50 text-gold-700 ring-1 ring-inset ring-gold-200",
  green: "bg-emerald-50 text-emerald-700",
} as const;

export function Chip({ children, tone = "slate", mono = false }: { children: ReactNode; tone?: keyof typeof CHIP_TONE; mono?: boolean }) {
  return (
    <span className={cx("inline-flex flex-none items-center whitespace-nowrap rounded-md px-1.5 py-0.5 text-[11px] font-semibold leading-4", CHIP_TONE[tone], mono && "num")}>
      {children}
    </span>
  );
}

const CALLOUT = {
  info: { box: "bg-sky-50 text-sky-900 ring-sky-200", icon: Info },
  warn: { box: "bg-amber-50 text-amber-900 ring-amber-200", icon: AlertTriangle },
  error: { box: "bg-rose-50 text-rose-900 ring-rose-200", icon: OctagonAlert },
} as const;

export function Callout({ kind = "warn", children }: { kind?: keyof typeof CALLOUT; children: ReactNode }) {
  const { box, icon: Icon } = CALLOUT[kind];
  return (
    <div className={cx("flex gap-2 rounded-xl px-3 py-2 text-[13px] leading-snug ring-1 ring-inset", box)}>
      <Icon className="mt-px h-4 w-4 flex-none" strokeWidth={2} />
      <div>{children}</div>
    </div>
  );
}

export function Skeleton({ className }: { className: string }) {
  return <div className={cx("skeleton", className)} />;
}

export function Spinner({ className = "h-4 w-4" }: { className?: string }) {
  return <span className={cx("inline-block animate-spin rounded-full border-2 border-current border-r-transparent", className)} aria-hidden />;
}

export function EmptyState({ icon, title, children }: { icon: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-navy-50 text-navy-700">{icon}</div>
      <div className="font-semibold text-slate-800">{title}</div>
      {children && <div className="mt-1 max-w-xs text-sm text-slate-500">{children}</div>}
    </div>
  );
}

export const VERDICT = {
  buy: { word: "Buy", solid: "bg-emerald-600 text-white", soft: "bg-emerald-50 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500", text: "text-emerald-700" },
  wait: { word: "Wait", solid: "bg-amber-500 text-white", soft: "bg-amber-50 text-amber-900 ring-amber-200", dot: "bg-amber-500", text: "text-amber-700" },
  pass: { word: "Pass", solid: "bg-rose-600 text-white", soft: "bg-rose-50 text-rose-800 ring-rose-200", dot: "bg-rose-500", text: "text-rose-700" },
} satisfies Record<Verdict, { word: string; solid: string; soft: string; dot: string; text: string }>;

export function VerdictPill({ verdict }: { verdict: Verdict }) {
  return (
    <span className={cx("inline-flex w-14 justify-center rounded-lg py-1 text-xs font-bold uppercase tracking-wide", VERDICT[verdict].solid)}>
      {VERDICT[verdict].word}
    </span>
  );
}

/** Up for green, down for red, flat for grey: "+3.2%". */
export function Change({ percent, className }: { percent: number | null | undefined; className?: string }) {
  if (percent == null) return <span className={cx("text-slate-400", className)}>-</span>;
  const tone = Math.abs(percent) < 0.05 ? "text-slate-500" : percent > 0 ? "text-emerald-600" : "text-rose-600";
  return (
    <span className={cx("num font-medium", tone, className)}>
      {percent > 0 ? "+" : percent < 0 ? "−" : ""}
      {Math.abs(percent).toFixed(1)}%
    </span>
  );
}

/** A card picture, or a quiet placeholder in the card's shape. */
export function CardImage({ src, alt, className }: { src: string | null | undefined; alt: string; className: string }) {
  if (!src) {
    return <div className={cx("aspect-[63/88] rounded-[6%] bg-gradient-to-br from-slate-100 to-slate-200", className)} aria-label="No picture" />;
  }
  return <img src={src} alt={alt} loading="lazy" className={cx("aspect-[63/88] rounded-[5%] bg-slate-100 object-cover", className)} />;
}
